// src/components/kolTabs/ImageUpload.js
//
// The image picker of Studio's former collection form, moved verbatim
// (031-03) for the post composer: drop / browse or a URL, a preview with
// Replace / Remove; aspectRatio 'video' | 'portrait' (4:5) | 'square'. Two
// optional props, the defaults keeping the old behaviour: `resize` — the
// [maxWidth, maxHeight] bound of the data url it hands back (1280 × 720 as
// before) — and `onFile(file)`, called with the ORIGINAL picked file (the
// composer derives its tiny public thumbnail from it). 031-08: `fit` —
// 'cover' (default) | 'contain' for the preview. 031-09: `allowUrl` (default
// true) — false hides the Upload / URL switch; `hideLabel` — the caller renders
// the label row itself; `variant: 'field'` — the post composer's photo field: a
// full-width 2:1 box, 1 px dashed, the empty prompt or the contained preview with
// a thin bottom bar holding Replace / Remove (upload only).

import React, { useRef, useState } from 'react';
import { Image, ImagePlus, Link as LinkIcon, Upload, X } from 'lucide-react';
import { resizeImage } from '../../lib/resizeImage';

export const ImageUpload = ({
  value, onChange, label = "Image", aspectRatio = "video", resize = [1280, 720], onFile, fit = "cover",
  allowUrl = true, hideLabel = false, variant = 'default',
}) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [inputMode, setInputMode] = useState('upload'); // 'upload' or 'url'
  const [urlInput, setUrlInput] = useState('');

  const handleFileSelect = async (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setUploading(true);
    onFile?.(file);
    try {
      const resizedImage = await resizeImage(file, resize[0], resize[1], 0.85);
      onChange(resizedImage);
    } catch (err) {
      console.error('Failed to process image:', err);
      const reader = new FileReader();
      reader.onloadend = () => onChange(reader.result);
      reader.readAsDataURL(file);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    handleFileSelect(file);
  };

  const handleUrlSubmit = () => {
    if (urlInput && (urlInput.startsWith('http://') || urlInput.startsWith('https://'))) {
      onChange(urlInput);
      setUrlInput('');
    }
  };

  const clearImage = () => {
    onChange(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const aspectClass =
    aspectRatio === 'portrait' ? 'aspect-[4/5]'
      : aspectRatio === 'square' ? 'aspect-square'
        : 'aspect-video';

  // 031-09: the post composer's photo field (upload only; the caller renders the label)
  if (variant === 'field') {
    return (
      <div data-testid="image-field">
        {value ? (
          <div className="relative aspect-[2/1] overflow-hidden rounded-2xl border border-white/[0.06] bg-[#1A1A1A]">
            <img src={value} alt="Preview" className="h-full w-full rounded-2xl object-contain" />
            <div className="absolute inset-x-0 bottom-0 flex justify-end gap-2 bg-gradient-to-t from-black/70 to-transparent px-3 py-2" data-testid="image-field-bar">
              <button type="button" onClick={() => fileInputRef.current?.click()} className="text-xs font-medium text-white/80 hover:text-white">
                Replace
              </button>
              <button type="button" onClick={clearImage} className="text-xs font-medium text-white/80 hover:text-white">
                Remove
              </button>
            </div>
          </div>
        ) : (
          <div
            role="button"
            tabIndex={0}
            aria-label="Add a photo"
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
            className={`relative aspect-[2/1] cursor-pointer rounded-2xl border border-dashed transition-all ${dragActive
              ? 'border-[#A3E635] bg-[#A3E635]/5'
              : 'border-white/[0.10] bg-white/[0.02] hover:border-white/[0.18] hover:bg-white/[0.04]'
              }`}
          >
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
              {uploading ? (
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/10 border-t-[#A3E635]" />
              ) : (
                <>
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/[0.04] border border-white/[0.06]">
                    <ImagePlus size={18} className="text-zinc-400" />
                  </span>
                  <span className="text-sm font-medium text-zinc-300">Add a photo</span>
                  <span className="text-xs text-zinc-600">PNG or JPG, up to 5 MB</span>
                </>
              )}
            </div>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={(e) => handleFileSelect(e.target.files?.[0])}
          className="hidden"
        />
      </div>
    );
  }

  return (
    <div>
      {!hideLabel && <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-zinc-400">{label}</label>
        {!value && allowUrl && (
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setInputMode('upload')}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${inputMode === 'upload' ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-white'
                }`}
            >
              <Upload size={12} className="inline mr-1" />
              Upload
            </button>
            <button
              type="button"
              onClick={() => setInputMode('url')}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${inputMode === 'url' ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-white'
                }`}
            >
              <LinkIcon size={12} className="inline mr-1" />
              URL
            </button>
          </div>
        )}
      </div>}

      {value ? (
        <div className={`relative ${aspectClass} rounded-2xl overflow-hidden bg-[#1A1A1A] border border-white/[0.06] group`}>
          <img src={value} alt="Preview" className={`w-full h-full ${fit === "contain" ? "object-contain" : "object-cover"}`} />
          <div className="absolute inset-0 flex items-end justify-center gap-3 bg-gradient-to-t from-black/80 via-transparent to-transparent pb-3 opacity-100 transition-opacity md:items-center md:bg-none md:bg-black/60 md:pb-0 md:opacity-0 md:group-hover:opacity-100">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white text-sm font-medium hover:bg-white/20 transition-colors flex items-center gap-2"
            >
              <Upload size={14} />
              Replace
            </button>
            <button
              type="button"
              onClick={clearImage}
              className="px-4 py-2 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 text-sm font-medium hover:bg-red-500/30 transition-colors flex items-center gap-2"
            >
              <X size={14} />
              Remove
            </button>
          </div>
        </div>
      ) : inputMode === 'upload' || !allowUrl ? (
        <div
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onClick={() => fileInputRef.current?.click()}
          className={`relative ${aspectClass} rounded-2xl border-2 border-dashed cursor-pointer transition-all ${dragActive
            ? 'border-[#A3E635] bg-[#A3E635]/5'
            : 'border-white/[0.08] bg-white/[0.02] hover:border-white/[0.15] hover:bg-white/[0.04]'
            }`}
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            {uploading ? (
              <div className="w-8 h-8 border-2 border-white/10 border-t-[#A3E635] rounded-full animate-spin" />
            ) : (
              <>
                <div className="w-12 h-12 rounded-xl bg-white/[0.04] border border-white/[0.06] flex items-center justify-center">
                  <Image size={20} className="text-zinc-500" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-zinc-400">
                    Drop an image or <span className="text-white">browse</span>
                  </p>
                  <p className="text-xs text-zinc-600 mt-1">PNG, JPG up to 5MB</p>
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <input
            type="text"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="https://example.com/image.jpg"
            className="w-full px-4 py-3 rounded-xl bg-white/[0.02] border border-white/[0.06] text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-white/20 transition-colors"
          />
          <button
            type="button"
            onClick={handleUrlSubmit}
            disabled={!urlInput}
            className="w-full py-2.5 rounded-xl bg-white/[0.06] border border-white/[0.06] text-white text-sm font-medium hover:bg-white/[0.1] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Add Image
          </button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => handleFileSelect(e.target.files?.[0])}
        className="hidden"
      />
    </div>
  );
};

export default ImageUpload;
